import { type Component, createEffect, For, Show } from 'solid-js'
import { createStore, produce } from 'solid-js/store'
import Button from '~/components/Button'
import Input from '~/components/Input'
import MaskInput from '~/components/MaskInput'
import Modal from '~/components/Modal'
import Switch from '~/components/Switch'
import type { TarifaModalDraftType, TarifaModalPropsType } from '~/types/TarifaModal'

const TarifaModal: Component<TarifaModalPropsType> = props => {
  const [draft, setDraft] = createStore<TarifaModalDraftType>({ name: '', isDefault: false, tiers: [] })

  createEffect(() => {
    const t = props.tariff
    if (t) {
      setDraft({ name: t.name, isDefault: t.isDefault, tiers: t.tiers.map(tr => ({ ...tr })) })
    } else {
      setDraft({ name: '', isDefault: false, tiers: [{ minQuantity: 1, amount: 0 }] })
    }
  })

  const isValid = () => draft.name.trim().length > 0 && draft.tiers.length > 0

  const handleSave = () => {
    if (!isValid()) return
    props.onSave({ name: draft.name.trim(), isDefault: draft.isDefault, tiers: draft.tiers.map(t => ({ ...t })) })
  }

  const addTier = () => {
    const tiers = draft.tiers
    const last = tiers[tiers.length - 1]
    setDraft('tiers', tiers.length, { minQuantity: (last?.minQuantity ?? 1) * 10, amount: last?.amount ?? 0 })
  }

  const removeTier = (index: number) => {
    if (draft.tiers.length <= 1) return
    setDraft(
      'tiers',
      produce(tiers => tiers.splice(index, 1))
    )
  }

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="sm" closable>
      <Modal.Header>
        <h2 class="text-lg font-bold text-foreground">{props.tariff ? 'Editar tarifa' : 'Nueva tarifa'}</h2>
      </Modal.Header>

      <Modal.Body>
        <div class="space-y-5">
          <Input label="Nombre de la tarifa" value={draft.name} onInput={e => setDraft('name', e.currentTarget.value)} placeholder="Público, Mayorista…" />

          <div class="flex items-center justify-between">
            <span class="text-sm text-foreground">Por defecto</span>
            <Switch checked={draft.isDefault} onChange={e => setDraft('isDefault', e.currentTarget.checked)} />
          </div>

          <div>
            <div class="flex items-center justify-between mb-2">
              <p class="text-sm font-medium text-foreground-subtle">Precios por cantidad</p>
              <Button size="sm" variant="text" color="info" onClick={addTier} prependIcon="material-symbols-light:add">
                Añadir
              </Button>
            </div>

            <div class="space-y-3">
              <For each={draft.tiers}>
                {(tier, i) => (
                  <div class="flex items-end gap-3">
                    <div class="w-32">
                      <MaskInput label="Desde" maskType="custom" customMask="99999" value={String(tier.minQuantity)} onValueChange={v => setDraft('tiers', i(), 'minQuantity', Number(v) || 1)} />
                    </div>
                    <div class="flex-1">
                      <MaskInput
                        label="Precio"
                        maskType="currency"
                        currency="COP"
                        locale="es-CO"
                        fractionDigits={0}
                        value={String(tier.amount)}
                        onValueChange={v => setDraft('tiers', i(), 'amount', Number(v) || 0)}
                      />
                    </div>
                    <Show when={draft.tiers.length > 1}>
                      <Button size="sm" variant="text" color="danger" icon="material-symbols-light:close" tooltip="Quitar tramo" onClick={() => removeTier(i())} />
                    </Show>
                  </div>
                )}
              </For>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="soft" color="default" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button type="button" variant="contained" color="primary" disabled={!isValid()} onClick={handleSave}>
            Guardar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default TarifaModal
